import { Popover as NextUIPopover } from '@nextui-org/react';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ButtonSize } from '../Button';
import {
  ButtonsContainer,
  ContentContainer,
  CountComponent,
  ItemsContainer,
  StyledApplyButton,
  StyledButton,
  StyledChevron,
  StyledCloseIcon,
  StyledIconButton,
  StyledSecondaryButton,
} from './CustomFilterDropdown.styles';

interface CustomFilterDropdownProps {
  label: string;
  triggerButtonSize?: ButtonSize;
  selectedItemsCount?: number;
  hideChevron?: boolean;
  iconLeft?: ReactNode;
  clearItemsOnClick?: () => void;
  children?: React.ReactNode;
  onApply?: () => void;
  onCancel?: () => void;
  /**
   * Optional callback that is called when the dropdown is opened.
   */
  onOpen?: () => void;
  submitButtonProps?: {
    title?: string;
    disabled?: boolean;
  };
}

const CustomFilterDropdown = ({
  label,
  triggerButtonSize,
  selectedItemsCount,
  hideChevron,
  iconLeft,
  clearItemsOnClick,
  children,
  onApply,
  onCancel,
  onOpen,
  submitButtonProps,
}: CustomFilterDropdownProps) => {
  // Dependencies
  const { t } = useTranslation();

  // State
  const [open, setOpen] = useState(false);

  // Handlers
  const handleOnOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      onCancel?.();
    } else {
      onOpen?.();
    }
    setOpen((prev) => !prev);
  };

  const handleOnCancel = () => {
    setOpen(false);
    onCancel?.();
  };

  const handleOnApply = () => {
    setOpen(false);
    onApply?.();
  };

  return (
    <NextUIPopover isOpen={open} onOpenChange={handleOnOpenChange} placement="bottom-left">
      <NextUIPopover.Trigger>
        <StyledButton size={triggerButtonSize ?? 'sm'} $isOpen={open} $hasSelectedOptions={selectedItemsCount > 0}>
          {iconLeft}
          {label}
          {selectedItemsCount > 0 ? (
            <>
              <CountComponent>{selectedItemsCount}</CountComponent>
              <StyledIconButton icon={<StyledCloseIcon />} onClick={clearItemsOnClick} />
            </>
          ) : (
            !hideChevron && <StyledChevron $isOpen={open} />
          )}
        </StyledButton>
      </NextUIPopover.Trigger>
      <ContentContainer>
        <ItemsContainer>{children}</ItemsContainer>
        <ButtonsContainer>
          <StyledSecondaryButton $disableMinWidth onPress={handleOnCancel}>
            {t('filters.cancel')}
          </StyledSecondaryButton>
          <StyledApplyButton $disableMinWidth disabled={submitButtonProps?.disabled} onPress={handleOnApply}>
            {submitButtonProps?.title || t('filters.applyFilter')}
          </StyledApplyButton>
        </ButtonsContainer>
      </ContentContainer>
    </NextUIPopover>
  );
};

export default CustomFilterDropdown;
